import React from "react";
import { Link } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import {
  FILTER_CARD_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_SEARCH_CLASS,
} from "../reports/commissions/commissionsConstants";
import { TASK_PAGE_SHELL_CLASS } from "../TaskManagement/workspaceConstants";

export const ATT_TABLE_HEAD =
  "sticky top-0 z-[1] border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500";
export const ATT_TABLE_HEAD_CENTER = `${ATT_TABLE_HEAD} text-center`;
export const ATT_TABLE_CELL =
  "border-b border-gray-100 px-4 py-3.5 align-middle text-[13px] text-gray-700";
export const ATT_TABLE_ROW = "transition-colors hover:bg-slate-50/90";

export const KPI_ICON_TONE = {
  primary: "bg-indigo-50 text-indigo-600",
  success: "bg-emerald-50 text-emerald-600",
  warning: "bg-amber-50 text-amber-600",
  danger: "bg-red-50 text-red-600",
  info: "bg-sky-50 text-sky-600",
};

const KPI_ACTIVE_RING = {
  primary: "ring-[#007BFF] border-[#007BFF]",
  success: "ring-emerald-500 border-emerald-400",
  warning: "ring-amber-500 border-amber-400",
  danger: "ring-red-500 border-red-400",
  info: "ring-sky-500 border-sky-400",
};

export function employeeInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

export function AttendancePageShell({ embedded, children }) {
  return (
    <div
      className={`tailwind-container w-full text-gray-900 ${
        embedded
          ? "-mx-2 w-[calc(100%+1rem)] max-w-none bg-[#f4f6f8] px-2 pb-4 pt-1 sm:-mx-3 sm:w-[calc(100%+1.5rem)] sm:px-3"
          : "bg-[#f4f6f8] px-2 pb-6 sm:px-3"
      }`}
    >
      <div className={`${TASK_PAGE_SHELL_CLASS} mx-auto w-full max-w-[1400px]`}>{children}</div>
    </div>
  );
}

export function AttendancePageHeader({ icon: Icon, title, subtitle, actions }) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[#007BFF] shadow-sm ring-1 ring-gray-200">
          <Icon size={20} />
        </span>
        <div className="min-w-0">
          <h1 className="m-0 text-xl font-bold text-[#35314d] sm:text-2xl">{title}</h1>
          {subtitle ? (
            <p className="m-0 mt-1 text-[13px] leading-snug text-gray-500">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function AttendanceKpiCard({
  label,
  value,
  icon: Icon,
  tone = "primary",
  loading,
  active = false,
  onClick,
}) {
  const ring = KPI_ACTIVE_RING[tone] || KPI_ACTIVE_RING.primary;
  const interactive = typeof onClick === "function";

  const body = (
    <>
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${KPI_ICON_TONE[tone] || KPI_ICON_TONE.primary}`}
      >
        <Icon size={15} />
      </span>
      <div className="min-w-0 flex-1 text-left leading-tight">
        <p className="m-0 truncate text-[10px] font-semibold text-gray-500">{label}</p>
        <p className="m-0 mt-0.5 text-lg font-bold tabular-nums text-gray-900">
          {loading ? "—" : value ?? 0}
        </p>
      </div>
    </>
  );

  const className = `relative flex min-w-0 flex-1 basis-0 items-center gap-2 rounded-lg border bg-white px-2 py-1.5 text-left shadow-sm transition ${
    active
      ? `ring-1 ${ring} bg-blue-50/30`
      : "border-[#E5E7EB] hover:border-gray-300 hover:bg-gray-50/50"
  } ${interactive ? "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#007BFF]" : ""}`;

  if (interactive) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-pressed={active}
        title={`${active ? "Clear" : "Show only"}: ${label}`}
      >
        {body}
      </button>
    );
  }

  return <div className={className}>{body}</div>;
}

export function AttendanceKpiGrid({ children }) {
  return <div className="mb-2 flex w-full gap-1.5 sm:gap-2">{children}</div>;
}

/** @deprecated use AttendanceKpiGrid */
export function AttendanceKpiStrip({ children }) {
  return <AttendanceKpiGrid>{children}</AttendanceKpiGrid>;
}

export function AttendanceFiltersPanel({
  title,
  subtitle,
  children,
  chips,
  onClearAll,
  showClearAll,
  clearAllLabel = "Clear all",
  compact = false,
}) {
  if (compact) {
    return (
      <div className="mb-2 overflow-hidden rounded-[10px] border border-[#E5E7EB] bg-white px-2.5 py-2 shadow-sm sm:px-3">
        {children}
        {chips?.length || showClearAll ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
            {chips}
            {showClearAll ? (
              <button
                type="button"
                onClick={onClearAll}
                className="ml-auto text-[11px] font-semibold text-[#007BFF] hover:underline"
              >
                {clearAllLabel}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`${FILTER_CARD_CLASS} mb-4 overflow-hidden p-0`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <h3 className="m-0 text-[13px] font-bold text-[#35314d]">{title}</h3>
        {showClearAll ? (
          <button
            type="button"
            onClick={onClearAll}
            className="text-[11px] font-semibold text-[#007BFF] hover:underline"
          >
            Clear all
          </button>
        ) : null}
      </div>
      <div className="px-3 py-2.5">{children}</div>
      {chips?.length ? (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 bg-gray-50/80 px-3 py-1.5">
          {chips}
        </div>
      ) : null}
    </div>
  );
}

export function AttendanceFilterCard({ children, footer }) {
  return (
    <div className={`${FILTER_CARD_CLASS} mb-4`}>
      <div className="rounded-lg bg-[#F1F5F9] p-3 sm:p-4">{children}</div>
      {footer ? <div className="mt-3 flex flex-wrap items-center justify-end gap-2 px-1 pb-1">{footer}</div> : null}
    </div>
  );
}

export function AttendanceFilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex max-w-[200px] items-center gap-0.5 rounded border border-[#007BFF]/20 bg-[#007BFF]/10 py-0 pl-1.5 pr-0.5 text-[10px] font-medium text-[#007BFF]">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#007BFF]/80 hover:bg-[#007BFF]/15"
        aria-label={`Remove ${label}`}
      >
        ×
      </button>
    </span>
  );
}

/**
 * One row, multiple columns. `template` is CSS grid-template-columns (e.g. "auto 1fr 1fr 2fr").
 */
export function AttendanceFilterRow({ template, children, minWidth = "34rem" }) {
  return (
    <div className="w-full overflow-x-auto">
      <div
        className="grid items-end gap-2"
        style={{ gridTemplateColumns: template, minWidth }}
      >
        {children}
      </div>
    </div>
  );
}

export function AttendanceFilterField({ label, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      {label ? <AttendanceCompactFilterLabel>{label}</AttendanceCompactFilterLabel> : null}
      {children}
    </div>
  );
}

export function AttendanceFilterQuickGroup({ children }) {
  return <div className="flex flex-nowrap items-center gap-1.5 self-end pb-0.5">{children}</div>;
}

export function AttendanceFilterLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className={FILTER_LABEL_CLASS}>
      {children}
    </label>
  );
}

const COMPACT_FIELD_CLASS =
  "h-8 w-full min-w-0 rounded-md border border-[#E5E7EB] bg-white px-2 text-[12px] text-gray-800 shadow-sm outline-none transition hover:border-gray-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

export function AttendanceSelect({ id, value, onChange, children, compact = true }) {
  return (
    <select
      id={id}
      value={value}
      onChange={onChange}
      className={compact ? COMPACT_FIELD_CLASS : "flex h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-[13px] text-gray-800 shadow-sm outline-none transition hover:border-gray-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"}
    >
      {children}
    </select>
  );
}

export function AttendanceDateInput({
  id,
  value,
  onChange,
  min,
  max,
  type = "date",
  compact = true,
  narrow = false,
  className = "",
}) {
  const narrowClass = narrow ? "w-[8.75rem] max-w-full shrink-0" : "";
  return (
    <input
      id={id}
      type={type}
      value={value}
      min={min}
      max={max}
      onChange={onChange}
      className={
        compact
          ? `${COMPACT_FIELD_CLASS} ${narrowClass} ${className}`.trim()
          : `flex h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-[13px] text-gray-800 shadow-sm outline-none transition hover:border-gray-300 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 ${className}`
      }
    />
  );
}

export function AttendanceSearchInput({ id, value, onChange, placeholder, compact = true }) {
  return (
    <input
      id={id}
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={compact ? COMPACT_FIELD_CLASS : FILTER_SEARCH_CLASS}
      autoComplete="off"
    />
  );
}

export function AttendanceDateChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 shrink-0 rounded-md px-2.5 text-[11px] font-semibold transition ${
        active
          ? "bg-[#007BFF] text-white"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {children}
    </button>
  );
}

export function AttendanceCompactFilterLabel({ children }) {
  return (
    <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </span>
  );
}

const btnBase =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-4 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";

export function AttendanceBtnPrimary({ children, ...props }) {
  return (
    <button type="button" className={`${btnBase} bg-[#007BFF] text-white hover:bg-[#0069D9]`} {...props}>
      {children}
    </button>
  );
}

export function AttendanceBtnGhost({ children, ...props }) {
  return (
    <button
      type="button"
      className={`${btnBase} border border-[#E5E7EB] bg-white text-gray-700 shadow-sm hover:bg-gray-50`}
      {...props}
    >
      {children}
    </button>
  );
}

export function AttendanceBtnOutline({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`${btnBase} border border-[#E5E7EB] bg-white text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function AttendanceTableCard({
  title,
  extra,
  children,
  footer,
  scrollable = true,
  flush = false,
}) {
  const bodyClass = scrollable
    ? "max-h-[min(70vh,720px)] overflow-auto"
    : "overflow-x-auto";

  if (flush) {
    return (
      <section className="overflow-hidden rounded-[10px] border border-[#E5E7EB] bg-white shadow-sm">
        {(title || extra) && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
            {title ? (
              <h2 className="m-0 text-[13px] font-bold text-[#35314d]">{title}</h2>
            ) : (
              <span />
            )}
            {extra}
          </div>
        )}
        <div className={bodyClass}>{children}</div>
        {footer ? (
          <div className="border-t border-gray-100 bg-gray-50/40 px-2 py-2 sm:px-3">{footer}</div>
        ) : null}
      </section>
    );
  }

  return (
    <div className="overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <h2 className="m-0 text-sm font-semibold text-[#35314d]">{title}</h2>
        {extra}
      </div>
      <div className={bodyClass}>{children}</div>
      {footer ? <div className="border-t border-gray-100 px-2 py-2">{footer}</div> : null}
    </div>
  );
}

export function AttendanceTableWrap({ children }) {
  return <table className="w-full min-w-[880px] border-collapse">{children}</table>;
}

export function AttendanceEmployeeCell({ name, employeeId, branchName }) {
  return (
    <div className="flex min-w-[200px] items-center gap-3">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-bold text-indigo-700"
        aria-hidden
      >
        {employeeInitials(name)}
      </span>
      <div className="min-w-0 flex-1">
        <Link
          to={`/attendance/${employeeId}`}
          className="group flex items-center gap-1 font-semibold text-[#35314d] hover:text-[#007BFF]"
        >
          <span className="truncate">{name}</span>
          <FaChevronRight
            size={10}
            className="shrink-0 text-gray-400 transition group-hover:text-[#007BFF]"
          />
        </Link>
        {branchName ? (
          <p className="m-0 mt-0.5 truncate text-[11px] text-gray-500">{branchName}</p>
        ) : null}
      </div>
    </div>
  );
}

export function AttendanceTimePill({ time, variant = "in" }) {
  if (!time) {
    return <span className="text-[13px] text-gray-400">—</span>;
  }
  const colors =
    variant === "out"
      ? "bg-violet-50 text-violet-800 ring-violet-100"
      : "bg-emerald-50 text-emerald-800 ring-emerald-100";
  return (
    <span
      className={`inline-flex rounded-md px-2 py-1 font-mono text-[12px] font-medium ring-1 ${colors}`}
    >
      {time}
    </span>
  );
}

export function AttendanceHoursBadge({ value }) {
  if (!value || value === "—") {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1 text-[12px] font-semibold tabular-nums text-slate-700">
      {value}
    </span>
  );
}

export function AttendanceEmpty({ icon: Icon, title, description }) {
  return (
    <div className="px-4 py-16 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
        <Icon size={26} />
      </div>
      <p className="m-0 text-base font-semibold text-gray-800">{title}</p>
      {description ? <p className="m-0 mt-2 text-[13px] text-gray-500">{description}</p> : null}
    </div>
  );
}

export function AttendancePermissionDenied() {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
      You do not have permission to view employee attendance.
    </div>
  );
}
